import type { FinancingProduct, PartnerSyncChecklist } from '../../../../store/oneButtonStore';
import type { CombinedFinancingSummary } from './financingCombinedSummary';
import {
    findSyncStatusRowForLeadOrProduct,
    isPartnerSyncCompleteForQuote,
    partnerRequiresQuoteSyncChecklist,
} from './partnerSyncConfig';
import {
    financePartnerKeysEqual,
    financePartnerNormLower,
} from './utils/finance';

export type GenerateQuoteSyncGateResult = {
    blocked: boolean;
    reason?: string;
};

export type GenerateQuoteSyncScope = {
    /** Grid / plan partner (e.g. GoodLeap, LightReach). */
    quotePartner?: string;
    /** Multiple partners for combined quotes. */
    quotePartners?: string[];
    /** Atomic products included in this quote attempt. */
    quoteProductTypes?: Array<'Solar' | 'Roof' | 'Battery'>;
};

export type GenerateQuoteSyncGateInput = {
    opportunityDetails: any | null | undefined;
    financePartnerSyncStatus: Record<string, PartnerSyncChecklist>;
    financePartnerRawSyncStatuses: any[] | null | undefined;
    scope?: GenerateQuoteSyncScope;
};

const SYNC_REQUIRED_MESSAGE = 'Complete Partner Sync before generating a quote.';
const LEAD_ID_REQUIRED_MESSAGE =
    'Complete Partner Sync (including lead save) before generating a quote.';

function partnersMatch(a: string | null | undefined, b: string | null | undefined): boolean {
    return financePartnerKeysEqual(a, b);
}

function activeOpportunityProducts(opportunityDetails: any | null | undefined): any[] {
    const raw = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    const arr = Array.isArray(raw) ? raw : [];
    return arr.filter((p: any) => p.ActiveFlag !== false && p.activeFlag !== false);
}

export function scopePartnerNames(scope?: GenerateQuoteSyncScope): string[] | null {
    if (!scope) return null;
    if (scope.quotePartners?.length) return scope.quotePartners;
    if (scope.quotePartner) return [scope.quotePartner];
    return null;
}

function relevantActiveProducts(
    opportunityDetails: any | null | undefined,
    scope?: GenerateQuoteSyncScope,
): any[] {
    let products = activeOpportunityProducts(opportunityDetails);
    if (scope?.quoteProductTypes?.length) {
        const types = new Set(scope.quoteProductTypes);
        products = products.filter((p: any) => types.has(p.Product_Type_Name));
    }
    const partnerNames = scopePartnerNames(scope);
    if (partnerNames?.length) {
        products = products.filter((p: any) =>
            partnerNames.some((partner) =>
                partnersMatch(String(p.Finance_Partner_Display_Name ?? ''), partner),
            ),
        );
    }
    return products;
}

function syncStatusForPartner(
    partner: string,
    statusMap: Record<string, PartnerSyncChecklist>,
): PartnerSyncChecklist | undefined {
    if (statusMap[partner]) return statusMap[partner];
    const norm = financePartnerNormLower(partner);
    for (const [key, value] of Object.entries(statusMap)) {
        if (financePartnerNormLower(key) === norm) return value;
    }
    return undefined;
}

function isIgsOrLightreachPartner(partner: string | null | undefined): boolean {
    const n = financePartnerNormLower(String(partner ?? ''));
    return n === 'igs' || n.includes('lightreach');
}

function checklistFromRawStatus(raw: any): Partial<PartnerSyncChecklist> {
    return {
        lead: raw?.IsLeadCreate === true,
        contact: raw?.IsContactSync === true,
        titleCheck: raw?.IsTilte === true || raw?.IsTitle === true,
        utility: raw?.IsUtility === true,
        leadSystem: raw?.IsLeadSystem === true,
        creditCheck: raw?.IsCreditPass === true,
    };
}

function rawIgsLightreachChecklistIncomplete(partial: Partial<PartnerSyncChecklist>): boolean {
    return (
        partial.lead !== true ||
        partial.contact !== true ||
        partial.utility !== true ||
        partial.leadSystem !== true
    );
}

function storeChecklistIncomplete(partial: PartnerSyncChecklist | undefined): boolean {
    return !isPartnerSyncCompleteForQuote(partial);
}

/** Atomic product types covered by a financing grid / AI plan column. */
export function atomicProductTypesFromFinancingProduct(
    col: FinancingProduct | string,
): Array<'Solar' | 'Roof' | 'Battery'> {
    const text = String(col ?? '');
    const types: Array<'Solar' | 'Roof' | 'Battery'> = [];
    if (text.includes('Solar')) types.push('Solar');
    if (text.includes('Roof')) types.push('Roof');
    if (text.includes('Battery')) types.push('Battery');
    if (types.length) return types;
    if (col === 'Roof') return ['Roof'];
    if (col === 'Battery') return ['Battery'];
    return ['Solar'];
}

export function buildQuoteSyncScopeFromCombinedSummary(
    summary: CombinedFinancingSummary,
    productTypesOverride?: Array<'Solar' | 'Roof' | 'Battery'>,
): GenerateQuoteSyncScope {
    const quotePartners = [...new Set(summary.lines.map((line) => line.partner))];
    const quoteProductTypes =
        productTypesOverride && productTypesOverride.length > 0
            ? productTypesOverride
            : [
                  ...new Set(
                      summary.lines.flatMap((line) =>
                          line.picks.flatMap((pick) =>
                              atomicProductTypesFromFinancingProduct(pick.product),
                          ),
                      ),
                  ),
              ];
    return { quotePartners, quoteProductTypes };
}

function partnersRequiringSyncFromScope(
    opportunityDetails: any | null | undefined,
    scope?: GenerateQuoteSyncScope,
): string[] {
    const scopedPartnerList = scopePartnerNames(scope);
    if (scopedPartnerList?.length) {
        return [
            ...new Set(
                scopedPartnerList.filter((partner) => partnerRequiresQuoteSyncChecklist(partner)),
            ),
        ];
    }

    const activePartners = activeOpportunityProducts(opportunityDetails)
        .map((p: any) => String(p.Finance_Partner_Display_Name ?? '').trim())
        .filter(Boolean);
    return [
        ...new Set(activePartners.filter((partner) => partnerRequiresQuoteSyncChecklist(partner))),
    ];
}

function evaluateScopedSyncIncomplete(
    input: GenerateQuoteSyncGateInput,
    partnersRequiringSync: string[],
    activeProducts: any[],
): GenerateQuoteSyncGateResult | null {
    const { opportunityDetails, financePartnerSyncStatus, financePartnerRawSyncStatuses } = input;
    if (!partnersRequiringSync.length) return null;

    const haveAnyIgsLightreach =
        activeProducts.some((p: any) => isIgsOrLightreachPartner(p.Finance_Partner_Display_Name)) ||
        partnersRequiringSync.some((partner) => isIgsOrLightreachPartner(partner));

    if (haveAnyIgsLightreach) {
        const leadId = opportunityDetails?.Finance_Partner_Sync_LeadID;
        if (leadId == null || String(leadId).trim() === '') {
            return { blocked: true, reason: LEAD_ID_REQUIRED_MESSAGE };
        }
    }

    const syncList = Array.isArray(financePartnerRawSyncStatuses)
        ? financePartnerRawSyncStatuses
        : [];

    let sawMatchingRawStatus = false;
    if (haveAnyIgsLightreach && syncList.length > 0) {
        const leadId = String(opportunityDetails?.Finance_Partner_Sync_LeadID ?? '').trim();
        for (const product of activeProducts) {
            if (!isIgsOrLightreachPartner(product?.Finance_Partner_Display_Name)) continue;
            const productTypeName = String(
                product?.Product_Type_Name ?? product?.product_Type_Name ?? '',
            ).trim();
            const matchingRow = findSyncStatusRowForLeadOrProduct(syncList, {
                leadId,
                productTypeName,
            });
            if (!matchingRow) continue;
            sawMatchingRawStatus = true;
            if (rawIgsLightreachChecklistIncomplete(checklistFromRawStatus(matchingRow))) {
                return { blocked: true, reason: SYNC_REQUIRED_MESSAGE };
            }
        }
    }

    let sawAnyStatus = false;
    for (const partner of partnersRequiringSync) {
        const partial = syncStatusForPartner(partner, financePartnerSyncStatus);
        if (!partial) continue;
        sawAnyStatus = true;
        if (storeChecklistIncomplete(partial)) {
            return { blocked: true, reason: SYNC_REQUIRED_MESSAGE };
        }
    }

    if (sawAnyStatus) return { blocked: false };

    if (haveAnyIgsLightreach && sawMatchingRawStatus) {
        return { blocked: false };
    }

    return { blocked: true, reason: SYNC_REQUIRED_MESSAGE };
}

/**
 * Same core gates as QuotesContractsCard add-quote preflight + SolarQuoteDetails lead id check.
 * When `scope` is set, only partners/products in this quote attempt are considered
 * (GoodLeap / Enfin / Cash / Service Finance never block).
 */
export function evaluateGenerateQuoteSyncGate(input: GenerateQuoteSyncGateInput): GenerateQuoteSyncGateResult {
    const { opportunityDetails, scope } = input;

    const scopedPartners = scopePartnerNames(scope);
    if (scopedPartners?.length) {
        const allScopedPartnersExempt = scopedPartners.every(
            (partner) => !partnerRequiresQuoteSyncChecklist(partner),
        );
        if (allScopedPartnersExempt) return { blocked: false };
    }

    const activeProducts = relevantActiveProducts(opportunityDetails, scope);

    if (!activeProducts.length) {
        // Scoped to a grid/plan partner with no matching active opp product — partner mismatch flow handles it.
        if (scopedPartners?.length) return { blocked: false };
        const partnersRequiringSync = partnersRequiringSyncFromScope(opportunityDetails, scope);
        if (!partnersRequiringSync.length) return { blocked: false };
        const scopedResult = evaluateScopedSyncIncomplete(
            input,
            partnersRequiringSync,
            activeProducts,
        );
        return scopedResult ?? { blocked: false };
    }

    const partnersRequiringSync = [
        ...new Set(
            activeProducts
                .map((p: any) => String(p.Finance_Partner_Display_Name ?? '').trim())
                .filter((partner) => partner && partnerRequiresQuoteSyncChecklist(partner)),
        ),
    ];

    if (!partnersRequiringSync.length) return { blocked: false };

    const scopedResult = evaluateScopedSyncIncomplete(
        input,
        partnersRequiringSync,
        activeProducts,
    );
    return scopedResult ?? { blocked: false };
}

/** First partner/product in scope that still needs sync (for opening the correct modal). */
export function findFirstIncompleteQuoteSyncTarget(
    input: GenerateQuoteSyncGateInput,
    scope?: GenerateQuoteSyncScope,
): { partner: string; productTypeName: 'Solar' | 'Roof' | 'Battery' } | null {
    const partners = scopePartnerNames(scope) ?? partnersRequiringSyncFromScope(input.opportunityDetails, scope);
    const productTypes = scope?.quoteProductTypes?.length
        ? scope.quoteProductTypes
        : (['Solar', 'Roof', 'Battery'] as const);

    for (const partner of partners) {
        if (!partnerRequiresQuoteSyncChecklist(partner)) continue;
        for (const productTypeName of productTypes) {
            const subScope: GenerateQuoteSyncScope = {
                quotePartner: partner,
                quoteProductTypes: [productTypeName],
            };
            if (evaluateGenerateQuoteSyncGate({ ...input, scope: subScope }).blocked) {
                return { partner, productTypeName };
            }
        }
    }

    if (scope && evaluateGenerateQuoteSyncGate({ ...input, scope }).blocked) {
        const partner = partners.find((p) => partnerRequiresQuoteSyncChecklist(p)) ?? partners[0];
        if (partner) {
            return { partner, productTypeName: productTypes[0] ?? 'Solar' };
        }
    }

    return null;
}
